// 強化された認証管理フック
// JWT自動更新、セッション復帰、多重ログイン検出などの機能を含む

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../toast/toastStore';
import apiService from '../services/api';
import { oauthService } from '../services/oauthService';
import { refreshSaasToken } from '../services/authInterceptors';
// saveToken（rtm_session_token）は dev-token 専用キー。OAuth 後は apiService.setToken（rtm_token）のみ使用するため import 不要。
import { useAppStore } from '../stores/appStore';
import { normalizeUser } from '../utils/normalizeUser';
import type { SaaSUser, LoginRequest, OAuthProvider } from '../types';

interface AuthState {
  user: SaaSUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  tokenExpiry: number | null;
  refreshing: boolean;
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  register: (userData: { name: string; email: string; password: string }) => Promise<boolean>;
  refreshToken: () => Promise<boolean>;
  updateProfile: (profileData: {
    name: string;
    email: string;
    company?: string;
    position?: string;
    timezone: string;
    language: string;
  }) => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  loginWithOAuth: (code: string, state: string, provider: OAuthProvider) => Promise<boolean>;
}

interface UseAuthReturn extends AuthState, AuthActions {}

// JWT デコード関数（簡易版）
function decodeJWT(token: string): { exp?: number; sub?: string; iat?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('JWT decode error:', error);
    return null;
  }
}

// セッション管理
class SessionManager {
  private static instance: SessionManager;
  private listeners: (() => void)[] = [];

  private constructor() {
    // 他のタブでのログアウト検出
    window.addEventListener('storage', (e) => {
      if (e.key === 'rtm_token' && !e.newValue) {
        this.notifyLogout();
      }
    });

    // 他のタブでのログイン検出
    window.addEventListener('storage', (e) => {
      if (e.key === 'rtm_token' && e.newValue && !e.oldValue) {
        this.notifyLogin();
      }
    });
  }

  public static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  public addListener(callback: () => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyLogout() {
    this.listeners.forEach(callback => callback());
  }

  private notifyLogin() {
    this.listeners.forEach(callback => callback());
  }
}

const sessionManager = SessionManager.getInstance();

export function useAuth(): UseAuthReturn {
  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
    tokenExpiry: null,
    refreshing: false,
  });

  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // トークン自動更新のスケジューリング
  const scheduleTokenRefresh = useCallback((expiry: number) => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // 有効期限の5分前にリフレッシュ
    const refreshTime = expiry * 1000 - Date.now() - 5 * 60 * 1000;
    
    if (refreshTime > 0) {
      refreshTimeoutRef.current = setTimeout(async () => {
        console.log('Attempting automatic token refresh');
        await refreshToken();
      }, refreshTime);
    }
  }, []);

  // トークン更新
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (state.refreshing) return false;

    setState(prev => ({ ...prev, refreshing: true }));

    const token = apiService.getToken();
    if (!token) {
      setState(prev => ({ ...prev, refreshing: false }));
      await logout();
      return false;
    }

    // getCurrentUser は localStorage から同期的に読み取る（ネットワークリクエストなし）
    const response = apiService.getCurrentUser();
    const payload = decodeJWT(token);
    // 将来 API 化した際に full_name が混入しても安全なよう正規化を通す
    const userData = response.success && response.data ? normalizeUser(response.data) : null;

    setState(prev => ({
      ...prev,
      user: userData ?? prev.user,
      isAuthenticated: true,
      token,
      tokenExpiry: payload?.exp || null,
      refreshing: false,
    }));

    if (payload?.exp) {
      scheduleTokenRefresh(payload.exp);
    }

    return true;
  }, [state.refreshing, scheduleTokenRefresh]);

  // 認証状態チェック
  const checkAuth = useCallback(async (): Promise<boolean> => {
    const token = apiService.getToken();
    
    if (!token) {
      setState(prev => ({ 
        ...prev, 
        isAuthenticated: false, 
        isLoading: false,
        user: null,
        token: null,
        tokenExpiry: null
      }));
      return false;
    }

    // JWT有効期限チェック
    const payload = decodeJWT(token);
    if (payload?.exp && payload.exp * 1000 <= Date.now()) {
      console.log('Token expired, attempting network refresh');
      // 実ネットワーク refresh を試みる（無条件 isAuthenticated:true を防ぐ）
      const newToken = await refreshSaasToken();
      if (!newToken) {
        // refresh 失敗: トークンをクリアして未認証扱い
        apiService.clearToken();
        localStorage.removeItem('rtm_refresh_token');
        localStorage.removeItem('rtm_session_token');
        useAppStore.getState().setUser(null);
        setState(prev => ({
          ...prev,
          isAuthenticated: false,
          isLoading: false,
          user: null,
          token: null,
          tokenExpiry: null,
        }));
        return false;
      }
      // refresh 成功: 新しいトークンで checkAuth を継続。
      // apiService の内部 token キャッシュを更新し、以降の state 更新でも新 token/payload を使う。
      apiService.setToken(newToken);
      const newPayload = decodeJWT(newToken);
      // getCurrentUser は localStorage から同期的に読み取る（ネットワークリクエストなし）
      const refreshedResponse = apiService.getCurrentUser();
      const refreshedUserData = refreshedResponse.success && refreshedResponse.data
        ? normalizeUser(refreshedResponse.data)
        : null;
      setState(prev => ({
        ...prev,
        user: refreshedUserData ?? prev.user,
        isAuthenticated: true,
        isLoading: false,
        token: newToken,
        tokenExpiry: newPayload?.exp || null,
      }));
      if (newPayload?.exp) {
        scheduleTokenRefresh(newPayload.exp);
      }
      return true;
    }

    // getCurrentUser は localStorage から同期的に読み取る（ネットワークリクエストなし）
    const response = apiService.getCurrentUser();
    // 将来 API 化した際に full_name が混入しても安全なよう正規化を通す
    const userData = response.success && response.data ? normalizeUser(response.data) : null;

    setState(prev => ({
      ...prev,
      user: userData ?? prev.user,
      isAuthenticated: true,
      isLoading: false,
      token,
      tokenExpiry: payload?.exp || null,
    }));

    if (payload?.exp) {
      scheduleTokenRefresh(payload.exp);
    }

    return true;
  }, [scheduleTokenRefresh]);

  // ログイン
  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await apiService.login(credentials);
      
      if (response.success && response.data) {
        const { access_token, refresh_token, user } = response.data;
        const token = access_token;

        apiService.setToken(token);
        // refresh_token を localStorage に保存（refresh-on-401 interceptor で使用）
        if (refresh_token) {
          localStorage.setItem('rtm_refresh_token', refresh_token);
        }
        // full_name → name 正規化を適用してから Zustand persist ストアに同期
        const normalizedUser = normalizeUser(user);
        useAppStore.getState().setUser(normalizedUser);
        const payload = decodeJWT(token);

        setState(prev => ({
          ...prev,
          user: normalizedUser,
          isAuthenticated: true,
          isLoading: false,
          token,
          tokenExpiry: payload?.exp || null,
        }));

        if (payload?.exp) {
          scheduleTokenRefresh(payload.exp);
        }

        toast.success('ログインしました');
        return true;
      } else {
        toast.error(response.error || 'ログインに失敗しました');
        setState(prev => ({ ...prev, isLoading: false }));
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('ログイン処理でエラーが発生しました');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [scheduleTokenRefresh]);

  // ログアウト
  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    }

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    // refresh_token も合わせてクリア
    localStorage.removeItem('rtm_refresh_token');
    // Zustand persist ストアのユーザー情報を消去
    useAppStore.getState().setUser(null);

    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      token: null,
      tokenExpiry: null,
      refreshing: false,
    });

    navigate('/auth/login');
    toast.success('ログアウトしました');
  }, [navigate]);

  // 新規登録
  const register = useCallback(async (userData: { 
    name: string; 
    email: string; 
    password: string; 
  }): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await apiService.register(userData);
      
      if (response.success && response.data) {
        const { access_token, refresh_token, user } = response.data;
        const token = access_token;

        apiService.setToken(token);
        // refresh_token を localStorage に保存（refresh-on-401 interceptor で使用）
        if (refresh_token) {
          localStorage.setItem('rtm_refresh_token', refresh_token);
        }
        // full_name → name 正規化を適用してから Zustand persist ストアに同期
        const normalizedUser = normalizeUser(user);
        useAppStore.getState().setUser(normalizedUser);
        const payload = decodeJWT(token);

        setState(prev => ({
          ...prev,
          user: normalizedUser,
          isAuthenticated: true,
          isLoading: false,
          token,
          tokenExpiry: payload?.exp || null,
        }));

        if (payload?.exp) {
          scheduleTokenRefresh(payload.exp);
        }

        toast.success('アカウントが作成されました。');
        return true;
      } else {
        toast.error(response.error || '新規登録に失敗しました');
        setState(prev => ({ ...prev, isLoading: false }));
        return false;
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error('新規登録処理でエラーが発生しました');
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [scheduleTokenRefresh]);

  // プロファイル更新
  const updateProfile = useCallback(async (profileData: {
    name: string;
    email: string;
    company?: string;
    position?: string;
    timezone: string;
    language: string;
  }): Promise<boolean> => {
    try {
      const response = await apiService.updateProfile(profileData);
      
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          user: response.data!,
        }));
        toast.success('プロファイルを更新しました');
        return true;
      } else {
        toast.error(response.error || 'プロファイル更新に失敗しました');
        return false;
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('プロファイル更新でエラーが発生しました');
      return false;
    }
  }, []);

  // OAuthコールバック処理
  const loginWithOAuth = useCallback(async (
    code: string,
    state: string,
    provider: OAuthProvider
  ): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await oauthService.handleOAuthCallback(code, state, provider);

      // SaaS JWT を rtm_token に保存する（api.ts interceptor が Authorization ヘッダーに使用）。
      // rtm_session_token（dev-token 専用キー）には書き込まない。
      // 会議開始フローの ensureValidToken → fetchDevToken が都度 rtm_session_token を埋める。
      apiService.setToken(response.access_token);

      // full_name → name 正規化を適用
      const normalizedUser = normalizeUser(response.user);
      // login / register と同様に Zustand persist ストアにも同期する
      useAppStore.getState().setUser(normalizedUser);
      const payload = decodeJWT(response.access_token);

      setState(prev => ({
        ...prev,
        user: normalizedUser,
        isAuthenticated: true,
        isLoading: false,
        token: response.access_token,
        tokenExpiry: payload?.exp || null,
      }));

      // SaaS JWT の自動更新は decodeJWT の `exp` クレーム依存（saveToken の expiresAt 明示管理を廃止）。
      // 標準 JWT は exp を持つため機能するが、exp 欠落時は scheduleTokenRefresh が走らない。
      if (payload?.exp) {
        scheduleTokenRefresh(payload.exp);
      }

      return true;
    } catch (error) {
      console.error('OAuth login error:', error);
      setState(prev => ({ ...prev, isLoading: false }));
      return false;
    }
  }, [scheduleTokenRefresh]);

  // 初回認証チェック
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // 他のタブでのセッション変更を監視
  useEffect(() => {
    const unsubscribe = sessionManager.addListener(() => {
      checkAuth();
    });

    return unsubscribe;
  }, [checkAuth]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // ネットワーク復旧時の認証再チェック
  useEffect(() => {
    const handleOnline = () => {
      if (state.isAuthenticated) {
        checkAuth();
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [state.isAuthenticated, checkAuth]);

  return {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    token: state.token,
    tokenExpiry: state.tokenExpiry,
    refreshing: state.refreshing,
    
    // Actions
    login,
    logout,
    register,
    refreshToken,
    updateProfile,
    checkAuth,
    loginWithOAuth,
  };
}